import dayjs from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import { ICON_SIZE, LABEL_COLOUR_HASH, AVATAR_BORDER } from '../../../../../global/constants';
import { StatusListInterface } from '../../../../../interfaces/ProjectInterface';
import { calculateDueTime, isEmpty } from '../../../../../helpers/common';
import {
  TaskContent,
  TaskItem,
  Form,
  Label,
  AssignGroup,
  StatusDropdown,
  ButtonIcon,
  IconHidden,
  Deadline,
  DeadBlock,
  Text,
  IconDueDate,
  MultipleLabel,
  DropBlock,
  DotIcon,
  DropLabel,
  TooltipContent
} from './styles';
import { AssignGroup1, AvatarImage } from '../styles';
import Avatar from '../../../../../component/avatar';
import AssignBoxV2 from '../../../../../component/dropdowns/assignPopup';
import { useTaskAssignActions } from '../../../../../component/dropdowns/assignPopup/assignActions';
import Tooltip from '../../../../../component/tooltip';
import { MainTooltipDiv, TooltipDiv } from '../../../tasks/styles';
import TaskStatusDropdownV2 from '../../../../../component/dropdowns/taskStatusDropdown';
import PriorityDropdownV2 from '../../../../../component/dropdowns/priorityDropdown';
import CheckboxInput from '../../../../../component/input/checkboxInput';
import { TaskModel } from '../../../../../mobx/models/task';
import { ProjectModel } from '../../../../../mobx/models/project';
import { UserModel } from '../../../../../mobx/models/user';

interface MilestoneTaskRowProps {
  task: TaskModel | undefined;
  currentProject: ProjectModel | undefined;
  onClickMilestoneTask: (task: TaskModel | undefined) => void;
  onClickTaskCheckBox: (task: TaskModel, value: boolean) => void;
  onClickUsers: (taskId: string) => void;
  userDetails: UserModel;
  maxVisibleLabels: number;
  onChangeStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
}

const MilestoneTaskRow: React.FC<MilestoneTaskRowProps> = ({
  task,
  currentProject,
  onClickMilestoneTask,
  onClickTaskCheckBox,
  onClickUsers,
  userDetails,
  maxVisibleLabels,
  onChangeStatus
}) => {
  const assignActions = useTaskAssignActions(task);
  // Memoize dueResult calculation - only recalculates when dueDate changes
  const dueResult = useMemo(() => {
    if (task?.dueDate) {
      return calculateDueTime(task.dueDate);
    }
    return undefined;
  }, [task?.dueDate]);

  // Memoize statusDetails lookup - only recalculates when statusId or project status data changes
  const statusDetails = useMemo<StatusListInterface | undefined>(() => {
    if (currentProject?.statusEnable && currentProject?.statusData?.length > 0) {
      if (!task?.statusId) {
        return currentProject?.defaultStatus;
      }
      return currentProject?.statusData?.find((x) => x['_id'] === task?.statusId);
    }
    return undefined;
  }, [task?.statusId, currentProject?.statusEnable, currentProject?.statusData, currentProject?.defaultStatus]);

  const handleUpdateStatus = useCallback(
    (status: StatusListInterface, previousStatus: StatusListInterface) => onChangeStatus(task!, status, previousStatus),
    [onChangeStatus, task]
  );
  return (
    <TaskContent role='button' onClick={() => onClickMilestoneTask(task)}>
      <IconHidden role='button' onClick={(e) => e.stopPropagation()}>
        <SVGIcon
          name='new-three-dots-vertical-icon'
          width={ICON_SIZE.MD}
          height={ICON_SIZE.MD}
          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
        />
      </IconHidden>
      <TaskItem>
        <Form>
          <StatusDropdown>
            {statusDetails ? (
              <TaskStatusDropdownV2
                defaultStatus={statusDetails}
                statusList={currentProject?.statusData || []}
                currentStatusId={task?.statusId}
                isTaskCheckBox={true}
                onUpdateStatus={handleUpdateStatus}
                isIconButton={true}
              />
            ) : (
              <ButtonIcon>
                <CheckboxInput
                  onChange={(e) => onClickTaskCheckBox(task!, !task?.status)}
                  checked={task?.status}
                  onClick={(e) => e.stopPropagation()}
                  checkboxType='task-checkbox'
                />
              </ButtonIcon>
            )}
            {currentProject?.priority?.enabled && (
              <PriorityDropdownV2
                taskData={task}
                currentProject={currentProject}
                isTaskCheckBox={true}
                isIconButton={true}
              />
            )}
          </StatusDropdown>
          <Label>{task?.name}</Label>
        </Form>
        <AssignGroup1>
          <AssignGroup role='button' onClick={(e) => e.stopPropagation()}>
            <ul role='button' onClick={() => onClickUsers(task?.id || '')}>
              {task?.users
                ?.filter((item) => !isEmpty(item?.id))
                ?.map((userProfile) => (
                  <li key={userProfile?.id}>
                    <AvatarImage>
                      <Avatar
                        size={20}
                        imgSrc={userProfile?.profile_photo || ''}
                        name={userProfile?.name}
                        borderVariant={AVATAR_BORDER.THIN}
                      />
                    </AvatarImage>
                  </li>
                ))}
              {task && (
                <AssignBoxV2
                  projectUsers={currentProject?.users || []}
                  context={{ mode: 'task', currentTask: task }}
                  onAssign={assignActions.onAssign}
                  onUnassign={assignActions.onUnassign}
                  isMultiAssignee={currentProject?.multiAssignee}
                />
              )}
            </ul>
          </AssignGroup>
        </AssignGroup1>
      </TaskItem>
      <Deadline hasGap={!task?.dueDate}>
        {!isEmpty(task?.labels) && (
          <MultipleLabel>
            {task?.labels?.map((label, index) => {
              if (index < maxVisibleLabels) {
                return (
                  <DropBlock className={index === 0 ? 'left-border' : ''} key={label?.id}>
                    <DotIcon color={`${LABEL_COLOUR_HASH[label?.color]}`} />
                    <DropLabel>{label?.name}</DropLabel>
                  </DropBlock>
                );
              } else if (index === maxVisibleLabels) {
                const remainingLabels = (task?.labels?.length || 0) - maxVisibleLabels;
                if (remainingLabels > 0) {
                  const tooltipContent = (
                    <MainTooltipDiv>
                      {task?.labels?.slice(maxVisibleLabels).map((l) => (
                        <TooltipDiv key={l.id}>
                          <DotIcon color={`${LABEL_COLOUR_HASH[l.color]}`} />
                          <span>{l.name}</span>
                        </TooltipDiv>
                      ))}
                    </MainTooltipDiv>
                  );
                  return (
                    <Tooltip title={tooltipContent} maxWidth={'max-content'} key={`more-${label?.id}`}>
                      <DropBlock>
                        <DropLabel>+{remainingLabels} more</DropLabel>
                      </DropBlock>
                    </Tooltip>
                  );
                } else {
                  return (
                    <DropBlock key={label?.id}>
                      <DotIcon color={`${LABEL_COLOUR_HASH[label?.color]}`} />
                      <DropLabel>{label?.name}</DropLabel>
                    </DropBlock>
                  );
                }
              }
              return null;
            })}
          </MultipleLabel>
        )}
        {task?.dueDate && (
          <DeadBlock hasLeftBorder={!isEmpty(task?.labels)}>
            <Tooltip
              title={
                <>
                  <div>Due {dueResult?.dueParam ?? ''}</div>
                  <div>Due on {dayjs(task?.dueDate).format(userDetails?.dateFormat)}</div>
                </>
              }
              placement='bottom'
              maxWidth={'max-content'}>
              <TooltipContent>
                <Text isOverdue={dueResult?.overDue && !task?.status}>{task?.dueDate && dueResult?.dueTime}</Text>
                <IconDueDate>
                  {dueResult?.overDue && !task?.status ? (
                    <SVGIcon
                      name='tasklist-calendar-duedate-icon'
                      width={ICON_SIZE.XXL}
                      height={ICON_SIZE.XXL}
                      viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                      className='calendar-due-fill-color'
                    />
                  ) : (
                    <SVGIcon
                      name='tasklist-calendar-icon'
                      width={ICON_SIZE.XXL}
                      height={ICON_SIZE.XXL}
                      viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                      className='calendar-fill-color'
                    />
                  )}
                </IconDueDate>
              </TooltipContent>
            </Tooltip>
          </DeadBlock>
        )}
      </Deadline>
    </TaskContent>
  );
};

export default observer(MilestoneTaskRow);
